import { Video, ExternalLink, Copy, CheckCircle, Clock, AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";
import { showSuccess, showError } from "@/utils/toastHelpers";
import { getErrorMessage } from "@/utils/errorUtils";

/**
 * Display Zoom meeting information for a telemedicine booking
 * Shows join link (for patients) or host link (for doctors) based on context
 */
export default function TelemedicineMeetingInfo({ booking, isHost = false }) {
  const [copied, setCopied] = useState(false);
  const queryClient = useQueryClient();

  const retryMeeting = useMutation({
    mutationFn: () =>
      apiClient.post(`/api/bookings/${booking.id}/zoom-retry`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      showSuccess("Video link regenerated");
    },
    onError: (error) => {
      showError("Couldn't regenerate video link", getErrorMessage(error));
    },
  });

  // Check if this is a telemedicine booking with Zoom details
  if (!booking?.isTelemedicine) {
    return null;
  }

  const { zoomMeeting, zoomStatus, zoomError } = booking;

  // Show pending state
  if (zoomStatus === "pending") {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-blue-700">
          <Clock className="w-5 h-5 animate-pulse" />
          <span className="font-medium">Setting up video meeting...</span>
        </div>
        <p className="text-sm text-blue-600 mt-1">
          The Zoom meeting is being created. Meeting details will be available shortly.
        </p>
      </div>
    );
  }

  // Show error state with retry option
  if (zoomStatus === "failed") {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-red-700">
          <AlertCircle className="w-5 h-5" />
          <span className="font-medium">Meeting setup failed</span>
        </div>
        <p className="text-sm text-red-600 mt-1">
          {zoomError || "Unable to create video meeting. Retry below or contact the practice."}
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => retryMeeting.mutate()}
          disabled={retryMeeting.isPending}
          className="gap-2 mt-3"
        >
          <RefreshCw
            className={`w-4 h-4 ${retryMeeting.isPending ? "animate-spin" : ""}`}
          />
          {retryMeeting.isPending ? "Retrying..." : "Retry video link"}
        </Button>
      </div>
    );
  }

  // No meeting details available
  if (!zoomMeeting || zoomStatus !== "created") {
    return null;
  }

  const meetingUrl = isHost ? zoomMeeting.hostUrl : zoomMeeting.meetingUrl;
  const buttonText = isHost ? "Start Meeting (Host)" : "Join Meeting";
  const buttonColor = isHost ? "bg-green-600 hover:bg-green-700" : "bg-blue-600 hover:bg-blue-700";

  const handleCopyLink = () => {
    navigator.clipboard.writeText(meetingUrl);
    setCopied(true);
    showSuccess("Meeting link copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-2">
        <div className="p-2 bg-blue-100 rounded-lg">
          <Video className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h4 className="font-medium text-blue-900">Telemedicine Appointment</h4>
          <p className="text-sm text-blue-600">Video consultation via Zoom</p>
        </div>
      </div>

      {zoomMeeting.password && (
        <div className="text-sm">
          <span className="text-slate-600 font-medium">Meeting Password: </span>
          <span className="font-mono bg-white px-2 py-1 rounded border border-slate-200">
            {zoomMeeting.password}
          </span>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          asChild
          className={`gap-2 flex-1 ${buttonColor}`}
        >
          <a href={meetingUrl} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="w-4 h-4" />
            {buttonText}
          </a>
        </Button>
        <Button
          variant="outline"
          onClick={handleCopyLink}
          className="gap-2"
        >
          {copied ? (
            <>
              <CheckCircle className="w-4 h-4 text-green-600" />
              Copied!
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              Copy Link
            </>
          )}
        </Button>
      </div>

      {isHost && (
        <p className="text-xs text-slate-500">
          Use this host link to start the meeting. The patient will join using their own link.
        </p>
      )}
    </div>
  );
}
